import { useEffect, useState, useMemo } from "react";
import { swalSuccess, swalError, swalConfirm } from "../../../utils/swalAlert.js";

import {
  fetchTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
} from "../../../api/admin/coreBasics";

import http from "../../../api/http";

import "../../../styles/AdminPanel/CoreBasic.css";

import EditModal from "../../AdminPanel/components/EditModal.jsx";

const PAGE_SIZE = 8;

export default function Templates() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const [form, setForm] = useState({
    title: "",
    folder_path: "",
    preview_url: "",
    description: "",
    is_active: true,
    thumbnailFile: null,
    thumbnailUrl: null,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState(null);

  const baseURL = http.defaults.baseURL;

  /* =========================================================
     IMAGE URL
     ========================================================= */

  const buildImage = (url) => {
    if (!url) return null;

    if (url.startsWith("http")) {
      return url;
    }

    return `${baseURL}${url}`;
  };

  /* =========================================================
     LOAD TEMPLATES
     ========================================================= */

  const load = async () => {
    try {
      const res = await fetchTemplates();
      setItems(res.data || []);
    } catch {
      swalError({ title: "خطا در دریافت تمپلیت‌ها" });
    }
  };

  useEffect(() => {
    load();
  }, []);

  /* =========================================================
     PAGINATION
     ========================================================= */

  const totalCount = items.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);

  useEffect(() => {
    if (page !== pageSafe) setPage(pageSafe);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount]);

  const pageNumbers = useMemo(() => {
    const nums = [];
    for (let n = 1; n <= totalPages; n++) nums.push(n);
    return nums;
  }, [totalPages]);

  const paged = useMemo(() => {
    const start = (pageSafe - 1) * PAGE_SIZE;
    return items.slice(start, start + PAGE_SIZE);
  }, [items, pageSafe]);

  /* =========================================================
     PREVIEW IMAGE
     ========================================================= */

  useEffect(() => {
    if (!form.thumbnailFile) {
      setPreviewSrc(buildImage(form.thumbnailUrl));
      return;
    }

    const objectUrl = URL.createObjectURL(form.thumbnailFile);
    setPreviewSrc(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [form.thumbnailFile, form.thumbnailUrl]);

  /* =========================================================
     ADD
     ========================================================= */

  const openAdd = () => {
    setEditing(null);

    setForm({
      title: "",
      folder_path: "",
      preview_url: "",
      description: "",
      is_active: true,
      thumbnailFile: null,
      thumbnailUrl: null,
    });

    setIsModalOpen(true);
  };

  /* =========================================================
     EDIT
     ========================================================= */

  const openEdit = (item) => {
    setEditing(item);

    setForm({
      title: item.title || "",
      folder_path: item.folder_path || "",
      preview_url: item.preview_url || "",
      description: item.description || "",
      is_active: item.is_active ?? true,
      thumbnailFile: null,
      thumbnailUrl: item.thumbnail || null,
    });

    setIsModalOpen(true);
  };

  /* =========================================================
     SAVE
     ========================================================= */

  const save = async () => {
    try {
      setLoading(true);

      const fd = new FormData();

      fd.append("title", form.title);
      fd.append("folder_path", form.folder_path);
      fd.append("preview_url", form.preview_url);
      fd.append("description", form.description);
      fd.append("is_active", form.is_active);

      if (form.thumbnailFile) {
        fd.append("thumbnail", form.thumbnailFile);
      }

      if (editing) {
        await updateTemplate(editing.template_id, fd);
      } else {
        if (!form.thumbnailFile) {
          setLoading(false);

          return swalError({ title: "لطفاً تصویر تمپلیت را انتخاب کنید" });
        }

        await createTemplate(fd);
      }

      await swalSuccess({ title: "تمپلیت ذخیره شد" });

      setIsModalOpen(false);
      setEditing(null);

      await load();
    } catch (err) {
      console.error(err);

      swalError({ title: "خطا در ذخیره اطلاعات" });
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     DELETE
     ========================================================= */

  const remove = async (id) => {
    const confirmed = await swalConfirm({
      title: "حذف تمپلیت؟",
      text: "این عملیات قابل بازگشت نیست",
      confirmText: "بله، حذف شود",
      cancelText: "انصراف",
      danger: true,
    });

    if (!confirmed) return;

    try {
      await deleteTemplate(id);

      await swalSuccess({ title: "تمپلیت حذف شد" });

      await load();
    } catch {
      swalError({ title: "خطا در حذف تمپلیت" });
    }
  };

  /* =========================================================
     RENDER
     ========================================================= */

  return (
    <>
      {/* =====================================================
          PAGE HEADER
          ===================================================== */}

      <div className="page-header-row">
        <h2 className="page-title">مدیریت تمپلیت‌ها</h2>

        <div className="add-button-wrapper">
          <button className="btn-glass" onClick={openAdd} type="button">
            افزودن تمپلیت
          </button>
        </div>
      </div>

      {/* =====================================================
          TABLE
          ===================================================== */}

      <div className="table-wrapper">
        <table className="user-table">
          <thead>
            <tr>
              <th>#</th>
              <th>تصویر</th>
              <th>عنوان</th>
              <th>پوشه</th>
              <th>پیش‌نمایش</th>
              <th>وضعیت</th>
              <th>عملیات</th>
            </tr>
          </thead>

          <tbody>
            {paged.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  style={{
                    textAlign: "center",
                    padding: "28px",
                    color: "rgba(43, 18, 76, 0.45)",
                  }}
                >
                  هیچ تمپلیتی ثبت نشده است
                </td>
              </tr>
            ) : (
              paged.map((it, i) => (
                <tr key={it.template_id}>
                  {/* شماره */}
                  <td>{(pageSafe - 1) * PAGE_SIZE + i + 1}</td>

                  {/* تصویر */}
                  <td>
                    {it.thumbnail ? (
                      <img
                        src={buildImage(it.thumbnail)}
                        className="table-thumb"
                        alt={it.title || "تصویر تمپلیت"}
                      />
                    ) : (
                      <span
                        style={{
                          color: "rgba(43, 18, 76, 0.35)",
                          fontSize: "12px",
                        }}
                      >
                        ---
                      </span>
                    )}
                  </td>

                  {/* عنوان */}
                  <td>{it.title || "---"}</td>

                  {/* مسیر پوشه */}
                  <td>
                    <code>{it.folder_path || "---"}</code>
                  </td>

                  {/* پیش نمایش */}
                  <td>
                    {it.preview_url ? (
                      <a
                        href={it.preview_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        مشاهده
                      </a>
                    ) : (
                      <span
                        style={{
                          color: "rgba(43, 18, 76, 0.35)",
                          fontSize: "12px",
                        }}
                      >
                        ---
                      </span>
                    )}
                  </td>

                  {/* وضعیت */}
                  <td>
                    <span
                      className={`template-status ${
                        it.is_active ? "is-active" : "is-inactive"
                      }`}
                    >
                      <span
                        className="template-status-dot"
                        aria-hidden="true"
                      />

                      {it.is_active ? "فعال" : "غیرفعال"}
                    </span>
                  </td>

                  {/* عملیات */}
                  <td>
                    <div className="actions-cell">
                      {/* ویرایش */}
                      <button
                        type="button"
                        className="icon-action icon-edit"
                        onClick={() => openEdit(it)}
                        title="ویرایش"
                        aria-label={`ویرایش ${it.title || "تمپلیت"}`}
                      >
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M12 20h9" />
                          <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                        </svg>
                      </button>

                      {/* حذف */}
                      <button
                        type="button"
                        className="icon-action icon-delete"
                        onClick={() => remove(it.template_id)}
                        title="حذف"
                        aria-label={`حذف ${it.title || "تمپلیت"}`}
                      >
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M3 6h18" />
                          <path d="M8 6V4h8v2" />
                          <path d="M19 6l-1 14H6L5 6" />
                          <path d="M10 11v5" />
                          <path d="M14 11v5" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {totalCount > 0 && (
          <div className="orders-pagination">
            <span className="pagination-info">
              {totalCount.toLocaleString("fa-IR")} تمپلیت — صفحه{" "}
              {pageSafe.toLocaleString("fa-IR")} از{" "}
              {totalPages.toLocaleString("fa-IR")}
            </span>
            <div className="pagination-controls">
              <button
                className="page-btn"
                disabled={pageSafe === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="صفحه قبل"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 6l-6 6 6 6" />
                </svg>
              </button>
              {pageNumbers.map((n) => (
                <button
                  key={n}
                  className={`page-btn number${n === pageSafe ? " active" : ""}`}
                  onClick={() => setPage(n)}
                >
                  {n.toLocaleString("fa-IR")}
                </button>
              ))}
              <button
                className="page-btn"
                disabled={pageSafe === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                aria-label="صفحه بعد"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* =====================================================
          EDIT / ADD MODAL
          ===================================================== */}

      {isModalOpen && (
        <EditModal onClose={() => setIsModalOpen(false)}>
          <h3 className="modal-title">
            {editing ? "ویرایش تمپلیت" : "افزودن تمپلیت"}
          </h3>

          <input
            type="text"
            placeholder="عنوان"
            value={form.title}
            onChange={(e) =>
              setForm({
                ...form,
                title: e.target.value,
              })
            }
          />

          <input
            type="text"
            placeholder="مسیر پوشه template"
            value={form.folder_path}
            onChange={(e) =>
              setForm({
                ...form,
                folder_path: e.target.value,
              })
            }
          />

          <input
            type="text"
            placeholder="آدرس پیش‌نمایش"
            value={form.preview_url}
            onChange={(e) =>
              setForm({
                ...form,
                preview_url: e.target.value,
              })
            }
          />

          <input
            type="file"
            accept="image/*"
            onChange={(e) =>
              setForm({
                ...form,
                thumbnailFile: e.target.files?.[0] || null,
              })
            }
          />

          {previewSrc && (
            <img
              src={previewSrc}
              className="preview-image"
              alt="پیش‌نمایش تمپلیت"
            />
          )}

          <textarea
            placeholder="توضیحات"
            value={form.description}
            onChange={(e) =>
              setForm({
                ...form,
                description: e.target.value,
              })
            }
          />

          <label className="checkbox">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) =>
                setForm({
                  ...form,
                  is_active: e.target.checked,
                })
              }
            />
            فعال
          </label>

          {/* دکمه‌های مودال */}
          <div className="modal-buttons">
            <button
              type="button"
              className="btn-glass"
              onClick={save}
              disabled={loading}
            >
              {loading ? "در حال ذخیره..." : "ذخیره"}
            </button>

            <button
              type="button"
              className="btn btn-cancel"
              onClick={() => setIsModalOpen(false)}
              disabled={loading}
            >
              انصراف
            </button>
          </div>
        </EditModal>
      )}
    </>
  );
}
